import { useQueryClient } from '@tanstack/react-query';
import type { AxiosProgressEvent } from 'axios';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';

import {
  type BannerViewModel,
  type EBannerPosition,
  type EBannerPriority,
  getApiV1BannerIdQueryOptions,
  getApiV1BannerQueryOptions,
  type PostApiV1BannerMutationRequest,
  usePostApiV1Banner,
  usePutApiV1BannerId,
} from '@/shared/api/generated';

import { BANNER_MEDIA_KIND, type BannerFormValues } from '../types/bannerForm';

/** Converte `YYYY-MM-DD` em ISO `date-time`, ou omite quando vazio. */
function toIsoDate(value: string): string | undefined {
  if (!value) return undefined;
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : undefined;
}

/**
 * Monta o payload de escrita (PascalCase, multipart) a partir do formulário.
 *
 * Três regras que vêm de decisões da change `add-banner-form`:
 *
 * - **`Position` vai sempre**, mesmo sem campo em tela: na criação com o valor
 *   default e, na edição, com a posição que já estava salva.
 * - **A imagem tem um arquivo por par**: `ImageMobile` é a arte que o app exibe (e
 *   o preview espelha) e `Image` é a do ambiente legado, com campo próprio na tela.
 *   Cada uma vai só se tiver sido escolhida.
 * - **O vídeo vai nos dois pares** (`Video`+`VideoMobile`) com o mesmo arquivo, para
 *   nenhum consumidor ficar sem a mídia.
 * - **O MP4 vai em `Video` e `VideoMobile`**, junto da duração lida do arquivo em
 *   `Duration`.
 */
export function buildBannerPayload(values: BannerFormValues): PostApiV1BannerMutationRequest {
  const isVideo = values.mediaKind === BANNER_MEDIA_KIND.Video;
  const duration =
    isVideo && values.videoDurationSeconds && values.videoDurationSeconds > 0
      ? values.videoDurationSeconds
      : values.duration;

  const startDate = toIsoDate(values.startDate);
  const endDate = toIsoDate(values.endDate);

  return {
    ...(values.companyId !== undefined ? { CompanyId: values.companyId } : {}),
    Title: values.title,
    ShowTitle: values.showTitle,
    ...(startDate ? { StartDate: startDate } : {}),
    ...(endDate ? { EndDate: endDate } : {}),
    Duration: duration,
    Priority: values.priority as EBannerPriority,
    Position: values.position as EBannerPosition,
    Url: values.url,
    General: values.general,
    IsMonograph: values.isMonograph,
    IsMonitor: values.isMonitor,
    Active: values.active,
    ...(!isVideo && values.imageFile ? { ImageMobile: values.imageFile } : {}),
    ...(!isVideo && values.imageDesktopFile ? { Image: values.imageDesktopFile } : {}),
    ...(isVideo && values.videoFile
      ? { Video: values.videoFile, VideoMobile: values.videoFile }
      : {}),
    ClassStudentIds: values.general ? [] : values.classStudents.map(cs => cs.classStudentId),
  };
}

/** Callbacks do salvamento de Banner. */
export interface SaveBannerCallbacks {
  readonly onSuccess?: (banner: BannerViewModel) => void;
  readonly onError?: () => void;
}

/**
 * Hook para criar/editar um Banner.
 *
 * Encapsula os mutations gerados pelo Kubb (`POST /api/v1/banner` e
 * `PUT /api/v1/banner/{id}`, ambos `multipart/form-data`), escolhendo a operação
 * pela presença de `id`, invalidando a listagem no sucesso e devolvendo o
 * `BannerViewModel` salvo — necessário para persistir as traduções em seguida
 * (os endpoints de idioma exigem `bannerId`).
 *
 * Enquanto o multipart sobe, `uploadProgress` acompanha os bytes enviados — é o que
 * alimenta a barra de progresso nos cards de mídia. Vale para a requisição do
 * banner; as traduções são gravadas depois, em requisições próprias.
 *
 * @example
 * ```tsx
 * const { save, isPending, uploadProgress } = useSaveBanner();
 * save(values, bannerId, { onSuccess: banner => saveTranslations(banner.id!, values) });
 * ```
 */
export function useSaveBanner() {
  const queryClient = useQueryClient();
  // Bytes enviados do multipart: é o único progresso real disponível (o axios
  // reporta o upload; o processamento no servidor não tem como ser medido daqui).
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  const handleUploadProgress = useCallback((event: AxiosProgressEvent) => {
    // Sem `total` não há percentual honesto a mostrar — a tela cai no indeterminado.
    if (!event.total) {
      setUploadProgress(null);
      return;
    }
    setUploadProgress(Math.min(100, Math.round((event.loaded / event.total) * 100)));
  }, []);

  const progressConfig = { client: { onUploadProgress: handleUploadProgress } };
  const createMutation = usePostApiV1Banner(progressConfig);
  const updateMutation = usePutApiV1BannerId(progressConfig);

  const save = (
    values: BannerFormValues,
    id: number | undefined,
    callbacks?: SaveBannerCallbacks,
  ) => {
    const onSuccess = (banner: BannerViewModel) => {
      setUploadProgress(null);
      void queryClient.invalidateQueries({ queryKey: getApiV1BannerQueryOptions().queryKey });
      // O detalhe também: é dele que a tela lê os caminhos da mídia recém-enviada,
      // para o card sair de "arquivo escolhido" e virar "mídia do cadastro".
      const savedId = banner.id ?? id;
      if (savedId !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: getApiV1BannerIdQueryOptions(savedId).queryKey,
        });
      }
      callbacks?.onSuccess?.(banner);
    };
    const onError = () => {
      setUploadProgress(null);
      callbacks?.onError?.();
    };

    // Zera antes de sair: sem isso a barra apareceria no percentual do save anterior.
    setUploadProgress(0);

    const payload = buildBannerPayload(values);

    if (id === undefined) {
      createMutation.mutate({ data: payload }, { onSuccess, onError });
      return;
    }

    // O cadastro não remove mídia: sem arquivo no payload, o que está lá permanece.
    // Trocar o tipo, então, é sempre uma substituição — o formulário exige o arquivo
    // novo antes de deixar salvar (ver `bannerFormSchema`).
    updateMutation.mutate({ id, data: payload }, { onSuccess, onError });
  };

  return {
    save,
    isPending: createMutation.isPending || updateMutation.isPending,
    /** Percentual de bytes enviados (0–100), ou `null` quando indeterminado. */
    uploadProgress,
  } as const;
}
